import React, { useState, useEffect } from 'react';
import { QrCode, Copy, AlertCircle, Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@vaiu/ui/alert';
import { Badge } from '@vaiu/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
import { useEthereum } from '@/context/EthereumContext';
import QRCode from 'react-qr-code';

const EthereumDepositFlow: React.FC = () => {
  const { toast } = useToast();
  const { serviceStatus } = useEthereum();
  const [amount, setAmount] = useState('');
  const [depositInfo, setDepositInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showGasWarning, setShowGasWarning] = useState(false);

  // Check current gas prices
  useEffect(() => {
    if (serviceStatus?.gasPrice) {
      const mediumGasGwei = parseInt(serviceStatus.gasPrice.medium) / 1e9;
      setShowGasWarning(mediumGasGwei > 50);
    }
  }, [serviceStatus]);

  const handleInitiateDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) return;

    setLoading(true);
    try {
      const info = await platformApi.ethereum.initiateDeposit(parseFloat(amount));
      setDepositInfo(info);
      toast({
        title: 'Deposit Initiated',
        description: 'Send ETH to the address shown below',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to initiate deposit',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = () => {
    if (depositInfo?.depositAddress) {
      navigator.clipboard.writeText(depositInfo.depositAddress);
      toast({
        title: 'Copied',
        description: 'Deposit address copied to clipboard',
      });
    }
  };

  if (depositInfo) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Complete Your Deposit</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-center">
            <QRCode value={depositInfo.qrCode} size={200} />
          </div>

          <div>
            <Label>Send exactly {amount} ETH to:</Label>
            <div className="flex items-center space-x-2 mt-1">
              <code className="flex-1 p-2 bg-muted rounded text-sm break-all">
                {depositInfo.depositAddress}
              </code>
              <Button variant="ghost" size="sm" onClick={copyAddress}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Important:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Send exactly {amount} ETH to the address above</li>
                <li>Your account will be credited after {depositInfo.confirmationsRequired || 12} confirmations</li>
                <li>Do not send any other tokens to this address</li>
                <li>Processing usually takes 3-5 minutes</li>
              </ul>
            </AlertDescription>
          </Alert>

          <Button
            variant="outline"
            className="w-full"
            onClick={() => setDepositInfo(null)}
          >
            New Deposit
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Deposit ETH</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="amount">Amount (ETH)</Label>
          <Input
            id="amount"
            type="number"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="0.001"
            max="10"
            step="0.001"
          />
          {amount && (
            <p className="text-sm text-muted-foreground mt-1">
              ≈ ${(parseFloat(amount) * 2000).toFixed(2)} USD
            </p>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          <Button variant="outline" size="sm" onClick={() => setAmount('0.01')}>
            0.01
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAmount('0.1')}>
            0.1
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAmount('0.5')}>
            0.5
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAmount('1')}>
            1.0
          </Button>
        </div>

        {/* Large Amount Warning */}
        {amount && parseFloat(amount) * 2000 > 5000 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Large Deposit Amount</AlertTitle>
            <AlertDescription>
              You're depositing a large amount (≈ ${(parseFloat(amount) * 2000).toFixed(2)} USD).
              Please double-check the amount and ensure you're sending to the correct address.
            </AlertDescription>
          </Alert>
        )}

        {/* Gas Price Warning */}
        {showGasWarning && (
          <Alert variant="default">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>High Network Fees</AlertTitle>
            <AlertDescription>
              Ethereum network fees are currently high. You'll pay more in gas fees for your deposit.
              Current gas: {serviceStatus?.gasPrice && (parseInt(serviceStatus.gasPrice.medium) / 1e9).toFixed(0)} Gwei
            </AlertDescription>
          </Alert>
        )}

        <Alert>
          <AlertDescription>
            <strong>Deposit Limits:</strong>
            <ul className="list-disc list-inside mt-1">
              <li>Minimum: 0.001 ETH</li>
              <li>Maximum: 10 ETH</li>
              <li>Network fees paid by sender</li>
            </ul>
          </AlertDescription>
        </Alert>

        <Button
          className="w-full"
          onClick={handleInitiateDeposit}
          disabled={loading || !amount || parseFloat(amount) <= 0}
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            'Generate Deposit Address'
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default EthereumDepositFlow;
